import React, { useState } from "react";
import { styled } from "@mui/material/styles";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Paper,
  TablePagination,
  Box,
  CircularProgress,
  Button,
  Grid,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { disableLab } from "../../../services/mentorServices/MentorServices";
import { toast } from "react-toastify";
import ConfirmDisableModal from "../../ConfirmDisableModal";
import FileUploadModal from "./FileUploadModal";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#7e7e7e",
    color: theme.palette.common.white,
    textTransform: "capitalize",
    fontSize: 16,
    fontWeight: 600,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    textTransform: "capitalize",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme, disabled }) => ({
  "&:nth-of-type(odd),&:nth-of-type(even)": {
    backgroundColor: disabled
      ? theme.palette.action.disabledBackground
      : theme.palette.action.hover,
    borderBottom: "1px solid rgba(224, 224, 224, 1)",
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

const LabPracticeTable = ({
  rowData,
  handleEditClick,
  getAllLabsData,
  loadingData,
  selectedCOE,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openModal, setOpenModal] = useState(false);
  const [selectedLab, setSelectedLab] = useState(null);
  const [openFileUpload, setFileUploadOpen] = useState(false);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleOpenModal = (lab) => {
    setSelectedLab(lab);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setSelectedLab(null);
  };

  const handleConfirmDisable = async () => {
    if (!selectedLab) return;
    try {
      const response = await disableLab(selectedLab.id);
      toast.success(response?.data.message);
      getAllLabsData();
    } catch (error) {
      toast.error(
        error?.response?.data.message || error?.message || error?.error
      );
    } finally {
      handleCloseModal();
    }
  };

  const paginatedRows = rowData.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleFileDownload = () => {
    try {
      const fileUrl = `${process.env.PUBLIC_URL}/SampleLabCSV.csv`;
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "SampleLabCSV.csv";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success("File downloaded successfully!");
    } catch (error) {
      toast.error("Failed to download the file.");
    }
  };

  const handleDataDownload = () => {
    if (rowData.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    const headers = [
      "lab_name",
      "lab_type",
      "startTime",
      "endTime",
      "main_mentor",
      "support_mentors",
    ];

    const csvRows = [
      headers.join(","),
      ...rowData.map((lab) => {
        const mainMentorName = lab.main_mentor?.email || "-";
        const supportMentorsNames =
          lab.support_mentors?.map((m) => m.email).join("; ") || "-";
        return [
          lab.lab_name || "-",
          lab.lab_type || "-",
          lab.startTime || "-",
          lab.endTime || "-",
          mainMentorName,
          supportMentorsNames,
        ].join(",");
      }),
    ];

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "LabData.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("Data CSV downloaded successfully!");
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Grid
        container
        spacing={1}
        justifyContent="flex-end"
        alignItems="center"
        flexWrap="wrap"
        mb="10px"
      >
        <Grid item>
          <Tooltip title="Download Sample CSV" arrow>
            <Button
              onClick={handleFileDownload}
              variant="contained"
              color="primary"
              sx={{ minWidth: 48, p: 1 }}
            >
              <DownloadIcon />
            </Button>
          </Tooltip>
        </Grid>

        <Grid item>
          <Tooltip title="Download Lab Data" arrow>
            <Button
              onClick={handleDataDownload}
              variant="contained"
              color="primary"
              sx={{ minWidth: 48, p: 1 }}
            >
              <DownloadIcon />
            </Button>
          </Tooltip>
        </Grid>

        <Grid item>
          <Tooltip title="Upload CSV" arrow>
            <Button
              variant="contained"
              color="primary"
              sx={{ minWidth: 48, p: 1 }}
              onClick={() => setFileUploadOpen(true)}
            >
              <UploadFileIcon />
            </Button>
          </Tooltip>
        </Grid>
      </Grid>
      <TableContainer
        component={Paper}
        className="table-container"
        sx={{ width: "100%", overflowX: "auto" }}
      >

        <Table
          sx={{ tableLayout: "auto", width: "max-content", minWidth: "100%" }}
          aria-label="customized table"
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>COE</StyledTableCell>
              <StyledTableCell>Lab Name</StyledTableCell>
              <StyledTableCell>Practice Time</StyledTableCell>
              <StyledTableCell>Lab Type</StyledTableCell>
              <StyledTableCell>Semester</StyledTableCell>
              <StyledTableCell>Enrolment Year</StyledTableCell>
              <StyledTableCell>Main Mentor</StyledTableCell>
              <StyledTableCell>Support Mentors</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {loadingData ? (
              <TableRow>
                <StyledTableCell colSpan={9} align="center">
                  <Box
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    height="30px"
                  >
                    <CircularProgress />
                  </Box>
                </StyledTableCell>
              </TableRow>
            ) : paginatedRows.length === 0 ? (
              <TableRow>
                <StyledTableCell colSpan={9} align="center">
                  No Lab Data
                </StyledTableCell>
              </TableRow>
            ) : (
              paginatedRows
                ?.sort(
                  (a, b) => new Date(b?.create_date) - new Date(a?.create_date)
                )
                .map((row, index) => (
                  <StyledTableRow key={index} disabled={row.disabled}>
                    <StyledTableCell>{row.base_coe?.name || "-"}</StyledTableCell>
                    <StyledTableCell>{row.lab_name || "-"}</StyledTableCell>
                    <StyledTableCell>
                      {row.startTime} - {row.endTime || "-"}
                    </StyledTableCell>
                    <StyledTableCell>{row.lab_type || "-"}</StyledTableCell>
                    <StyledTableCell>{row.semester || "-"}</StyledTableCell>
                    <StyledTableCell>{row.enrolment_year || "-"}</StyledTableCell>
                    <StyledTableCell>{row.main_mentor?.email || "-"}</StyledTableCell>
                    <StyledTableCell>
                      {row.support_mentors && row.support_mentors.length > 0
                        ? row.support_mentors.map((mentor) => mentor.email).join(", ")
                        : "-"}
                    </StyledTableCell>
                    <StyledTableCell>
                      <Tooltip title="Edit" arrow>
                        <IconButton
                          size="small"
                          aria-label="edit"
                          onClick={() => handleEditClick(row)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Disable" arrow>
                        <IconButton
                          size="small"
                          aria-label="disable"
                          onClick={() => handleOpenModal(row)}
                          disabled={row.disabled}
                        >
                          <BlockIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
            )}
          </TableBody>

        </Table>

        <TablePagination
          rowsPerPageOptions={[10,20,30,60,100]}
          component="div"
          count={rowData.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </TableContainer>

      <ConfirmDisableModal
        open={openModal}
        onClose={handleCloseModal}
        onConfirm={handleConfirmDisable}
        message={`lab "${selectedLab?.lab_name}"`}
      />
      <FileUploadModal
        open={openFileUpload}
        handleClose={() => setFileUploadOpen(false)}
        selectedCOE={selectedCOE}
        getAllLabsData={getAllLabsData}
      />
    </Box>
  );
};

export default LabPracticeTable;
